/**
 * All 3 services. Still backs the entire still-mocked onboarding funnel and investor dashboard
 * (Phase 9/11) — kept fully independent of `ServiceType` below (the real backend enum) rather than
 * aliased, since the two have different value sets and touching every onboarding/investor
 * consumer's string literals is out of this phase's scope.
 */
export type FlowType = 'audit' | 'trades' | 'build'

/**
 * Real backend enum (`ServiceType`, `prisma/schema.prisma`) — used only by the real `CaseSummary`/
 * `CaseDetail` data this phase introduces. `build_portfolio` has no dedicated UI anywhere yet —
 * kept here for exhaustiveness so `Record<ServiceType, ...>` consts don't need a `Partial`, not
 * because anything renders it (Phase 11 territory).
 */
export type ServiceType = 'portfolio_audit' | 'trades_audit' | 'build_portfolio'

export type CaseStatus =
  | 'submitted'
  | 'consultation_scheduled'
  | 'consultation_completed'
  | 'audit_ready'
  | 'published'

export type HoldingCategory = 'Equity' | 'Debt' | 'Hybrid' | 'Gold'
export type TradeSegment = 'Equity' | 'F&O' | 'Commodity'
export type DocumentKind = 'cas' | 'tradebook' | 'report'
export type DocumentStatus = 'received' | 'parsed' | 'failed'

/** Real backend enums — used only by the real `CaseHolding`/`CaseTrade`/`CaseDocumentSummary` shapes. */
export type CaseHoldingCategory = 'equity' | 'debt' | 'hybrid' | 'gold'
export type CaseTradeSegment = 'equity' | 'fno' | 'commodity'
export type CaseTradeSide = 'buy' | 'sell'
export type CaseDocumentKind = 'cas_pdf' | 'broker_xlsx' | 'broker_pdf' | 'advisor_report'
export type CaseUploaderType = 'investor' | 'staff'

export interface Investor {
  id: string
  name: string
  email: string
  phone: string
  pan: string
  city: string
  createdAt: string
}

/**
 * Real backend enum (`SeriousnessStatus`) — where a lead sits in the pre-case follow-up pipeline,
 * independent of `CaseStatus`, which tracks the case itself once one exists.
 */
export type SeriousnessStatus = 'pending' | 'qualified' | 'not_needed' | 'failed'

/** @deprecated Mock-era alias for {@link SeriousnessStatus} — kept for the still-mocked investor flows. */
export type LeadStatus = SeriousnessStatus

/**
 * Mock-era, single-shape case record — still backs the deferred investor-dashboard mock functions
 * (`fetchInvestorCase`, `fetchInvestorCases`, etc., `features/cases/api/cases.api.ts`) until Phase 9
 * replaces them. Real staff/admin data uses `CaseSummary`/`CaseDetail` instead (below) — the two
 * DTOs the backend itself returns, not a single record shape translated at the boundary.
 */
export interface CaseRecord {
  id: string
  /** Human-facing case number the investor quotes on a call, e.g. NVX-2026-0007. */
  reference: string
  investorId: string
  flow: FlowType
  status: CaseStatus
  /** 0-100 — drives the `ScoreProgress` gauge on the case's prescription. */
  wealthScore: number
  staffId: string | null
  updatedAt: string
  consultationSlot?: string
  leadStatus: LeadStatus
  /** Free-text note a staff member leaves for whoever picks up the case next. */
  note?: string
  /** Set once `sendLoginCredentials` has fired for this case, so staff can't double-send. */
  credentialsSent?: boolean
}

/** `GET /cases` row shape (`CaseSummaryDto`) — real backend data, staff/admin only. */
export interface CaseSummary {
  id: string
  reference: string
  serviceType: ServiceType
  caseStatus: CaseStatus
  seriousnessStatus: SeriousnessStatus
  investorId: string
  investorName: string | null
  advisorId: string | null
  advisorName: string | null
  contactMobile: string
  nextFollowUpAt: string | null
  createdAt: string
}

/** `GET /cases/:id`, `GET /cases/:id/preview` shape (`CaseDetailDto`) — real backend data. */
export interface CaseDetail {
  id: string
  reference: string
  serviceType: ServiceType
  origin: 'investor_web' | 'staff_manual'
  caseStatus: CaseStatus
  advisor: { id: string; name: string | null } | null
  contactMobile: string
  consultationSlotAt: string | null
  wealthScore: number | null
  trackingToken: string | null
  publishedAt: string | null
  loginDetailsSentAt: string | null
  /** Outcome of the most recent "Send Login Details" attempt, null if never attempted. Resend is
   * always allowed regardless of this value. */
  loginDetailsLastEmailSent: boolean | null
  createdAt: string
  updatedAt: string
  counts: {
    holdings: number
    trades: number
    prescriptionSteps: number
    documents: number
    goals: number
  }
  /** Present only for staff/admin viewers — absent for an investor viewing their own case. */
  seriousnessStatus?: SeriousnessStatus
  note?: string | null
  nextFollowUpAt?: string | null
  investor?: {
    id: string
    name: string | null
    email: string | null
    mobile: string
    pan: string | null
    city: string | null
    /** General investor attribute (not Build-Portfolio-exclusive) — Build's goal-math needs it to
     * compute a years-to-goal projection; set via the "Edit investor details" dialog. */
    age: number | null
  }
  /** Build Portfolio's income/expense intake — null for the other two service types, and null for
   * a Build Portfolio case that hasn't had its financial profile entered yet (e.g. an Add-Lead-
   * created case). */
  buildProfile: CaseBuildProfile | null
}

export interface CaseStatusCount {
  status: CaseStatus
  count: number
}

/** Mirrors `SaveInvestorProfileDto` — unlike `CreateCaseInput`'s `pan`, this one is required:
 * Save Profile is the point a login-capable investor account is created, and PAN is the identity
 * check for that. */
export interface SaveInvestorProfileInput {
  name: string
  email: string
  mobile: string
  pan: string
  city?: string
  age?: number
}

/** `POST /cases/:id/send-login-details` response — no password field in production, unlike the
 * admin staff-account equivalent (`admin-users.api.ts`'s `sendLoginDetails`). Outside production
 * the backend includes `password` as a dev/e2e-only convenience (gated server-side on `NODE_ENV`)
 * so local/e2e sign-in doesn't need a real mailbox — no production UI ever reads this field. */
export interface SendInvestorLoginDetailsResult {
  sentAt: string
  emailSent: boolean
  password?: string
}

export interface Holding {
  id: string
  caseId: string
  scheme: string
  category: HoldingCategory
  units: number
  investedValue: number
  currentValue: number
  xirr: number
}

/** `GET /cases/:id/holdings` row shape (`HoldingDto`) — Decimal fields serialize as strings. */
export interface CaseHolding {
  id: string
  scheme: string
  category: CaseHoldingCategory
  units: string
  investedValue: string
  currentValue: string
  xirr: string
  createdAt: string
  updatedAt: string
}

export interface Trade {
  id: string
  caseId: string
  symbol: string
  segment: TradeSegment
  side: 'BUY' | 'SELL'
  qty: number
  price: number
  tradedAt: string
  pnl: number
}

/** `GET /cases/:id/trades` row shape (`TradeDto`) — Decimal fields serialize as strings. */
export interface CaseTrade {
  id: string
  symbol: string
  segment: CaseTradeSegment
  side: CaseTradeSide
  qty: string
  price: string
  pnl: string
  tradedAt: string
}

export interface CaseDocument {
  id: string
  caseId: string
  kind: DocumentKind
  fileName: string
  sizeKb: number
  uploadedAt: string
  uploadedBy: 'investor' | 'staff'
  status: DocumentStatus
  /**
   * Object URL for a file uploaded in this browser session, so it can
   * actually be opened. Seeded documents have none — nothing is persisted
   * server-side until a real backend wires up storage.
   */
  objectUrl?: string
}

/**
 * `GET /cases/:id/documents` row shape (`CaseDocumentSummaryDto`) — no `status`/`objectUrl`
 * field exists backend-side yet (upload/download storage lands in Phase 7).
 */
export interface CaseDocumentSummary {
  id: string
  kind: CaseDocumentKind
  uploadedBy: CaseUploaderType
  fileName: string
  mimeType: string
  sizeBytes: number
  createdAt: string
}

export type StaffRole = 'advisor' | 'admin'

export interface Staff {
  id: string
  name: string
  email: string
  role: StaffRole
}

export type CallOutcome = 'connected' | 'no_answer' | 'follow_up_scheduled' | 'completed'

export interface CallLog {
  id: string
  investorId: string
  caseId?: string
  staffId: string
  calledAt: string
  durationMin: number
  outcome: CallOutcome
  notes?: string
  /** Absent until a telephony integration exists. */
  recordingUrl?: string
}

/** Mirrors `CreateHoldingDto` — decimal fields are fixed-precision strings, matching the backend's
 * Decimal columns. `case-forms.schema.ts`'s `createCaseHoldingSchema` produces this shape from
 * plain numeric UI input via `.transform((v) => v.toFixed(n))`. */
export interface CreateCaseHoldingInput {
  scheme: string
  category: CaseHoldingCategory
  units: string
  investedValue: string
  currentValue: string
  xirr: string
}

/** Mirrors `UpdateHoldingDto` — all fields optional, sent only for what actually changed. */
export type UpdateCaseHoldingInput = Partial<CreateCaseHoldingInput>

/** Mirrors `CreateTradeDto`. */
export interface CreateCaseTradeInput {
  symbol: string
  segment: CaseTradeSegment
  side: CaseTradeSide
  qty: string
  price: string
  pnl: string
  tradedAt: string
}

/** Mirrors `UpdateTradeDto` — all fields optional, sent only for what actually changed. */
export type UpdateCaseTradeInput = Partial<CreateCaseTradeInput>

export type PrescriptionStepPriority = 'high' | 'medium' | 'low'

/** Mirrors `CreatePrescriptionStepDto` — no `position` field; the backend always appends. */
export interface CreateCasePrescriptionStepInput {
  title: string
  description: string
  priority: PrescriptionStepPriority
  impactPoints: number
}

/** Mirrors `UpdatePrescriptionStepDto` — all fields optional, each caller sends only the ones it
 * means to change: the done-toggle sends `done`, the Up/Down reorder sends `position`, and the
 * edit-step dialog sends `title`/`description`/`priority`/`impactPoints`. */
export interface UpdateCasePrescriptionStepInput {
  title?: string
  description?: string
  priority?: PrescriptionStepPriority
  impactPoints?: number
  position?: number
  done?: boolean
}

export interface PrescriptionStep {
  id: string
  caseId: string
  text: string
  createdAt: string
  /** Short heading shown above `text` on the step card — absent on plain staff-authored steps. */
  title?: string
  priority?: PrescriptionStepPriority
  /** Points this step would add toward the case's wealth score, shown as a step-card footer. */
  impactPoints?: number
  done?: boolean
  /** True only when the investor's own toggle (not staff's) most recently set `done: true`. */
  doneByInvestor?: boolean
}

/** `GET /cases/:id/prescription-steps` row shape (`PrescriptionStepDto`), ordered by `position`. */
export interface CasePrescriptionStep {
  id: string
  title: string
  description: string
  priority: PrescriptionStepPriority
  impactPoints: number
  position: number
  done: boolean
  /** True only when the investor's own toggle (not staff's) most recently set `done: true`.
   * Locks staff's mark/unmark control until the investor undoes it. */
  doneByInvestor: boolean
}

export type GoalKey = 'wealth' | 'retirement' | 'home' | 'education' | 'marriage'
export type GoalPriority = 'high' | 'medium' | 'low'
export type GoalPlanStatus = 'pending' | 'approved'
/** Computed server-side (`goal-math.ts`) — `'not-applicable'` covers both `wealth` goals (no fixed
 * ₹ target, ever) and any goal whose target can't be computed yet (investor's age not set). */
export type GoalStatus = 'on-track' | 'at-risk' | 'completed' | 'not-applicable'

/** One recommended fund within a goal's plan (`GoalFundDto`). */
export interface CaseGoalFund {
  id: string
  fundName: string
  monthlySip: string
}

/** `GET /cases/:id/goals` row shape (`GoalDto`). Decimal fields (`presentCost`, `monthlyExpense`,
 * `invested`, each fund's `monthlySip`) serialize as strings. `yearsToGoal`/`target`/`status`/
 * `totalMonthlySip` are computed server-side, never re-derived here — see `goal-math.ts`'s
 * single-source-of-truth doc comment. */
export interface CaseGoal {
  id: string
  key: GoalKey
  childName: string | null
  targetAge: number | null
  presentCost: string | null
  monthlyExpense: string | null
  years: number | null
  invested: string | null
  /** A rate (0.06), not a percentage (6) — `null` means "no override," i.e. today's raw,
   * un-inflated target. Never meaningful for `wealth`. */
  inflationRate: number | null
  priority: GoalPriority | null
  funds: CaseGoalFund[]
  /** Sum of `funds[].monthlySip` — `'0.00'` when there are no funds yet, never `null`. */
  totalMonthlySip: string
  planStatus: GoalPlanStatus | null
  planNote: string | null
  planUpdatedAt: string | null
  createdAt: string
  updatedAt: string
  yearsToGoal: number | null
  target: string | null
  status: GoalStatus
}

/** Mirrors `CreateGoalDto` — which fields are actually required depends on `key`, enforced by
 * `case-forms.schema.ts`'s discriminated `createGoalSchema`, not by this type. Never carries
 * `priority`/`invested` — both are staff-set-only, added later via `UpdateCaseGoalInput`. */
export interface CreateCaseGoalInput {
  key: GoalKey
  childName?: string
  targetAge?: number
  presentCost?: string
  monthlyExpense?: string
  years?: number
}

/** Mirrors `UpdateGoalDto` — all optional, sent only for what changed. No `key` (a goal's type
 * never changes after creation) and no `planStatus` (that's the dedicated `.../approve` endpoint,
 * see `SetCaseGoalPlanApprovedInput`). Fund/SIP data no longer flows through this input at all —
 * see `CreateCaseGoalFundInput`/`UpdateCaseGoalFundInput`. */
export interface UpdateCaseGoalInput {
  childName?: string
  targetAge?: number
  presentCost?: string
  monthlyExpense?: string
  years?: number
  invested?: string
  /** A rate (0.06), not a percentage (6). `undefined` = leave unchanged (omitted from the
   * request entirely); `null` = explicitly clear a previously-set override back to unset. */
  inflationRate?: number | null
  priority?: GoalPriority
  planNote?: string
}

/** Mirrors `AddGoalFundDto` — both required, matching the "Attach plan" form's existing
 * "fund + SIP together" rule. */
export interface CreateCaseGoalFundInput {
  fundName: string
  monthlySip: string
}

/** Mirrors `UpdateGoalFundDto` — both optional, at least one required (enforced server-side). */
export interface UpdateCaseGoalFundInput {
  fundName?: string
  monthlySip?: string
}

/** `GET`/`PUT /cases/:id/build-profile` shape (`BuildProfileDto`) — embedded on `CaseDetail` as
 * `buildProfile`, `null` until staff fills it in. */
export interface CaseBuildProfile {
  monthlyIncome: string
  rentEmi: string
  utilitiesGroceries: string
  transportLifestyle: string
  otherExpenses: string
  createdAt: string
  updatedAt: string
}

/** Mirrors `UpsertBuildProfileDto` — a create-or-update, all 5 fields always required (there's no
 * separate partial-update shape for this one, matching the intake wizard's own all-required form). */
export interface UpsertCaseBuildProfileInput {
  monthlyIncome: string
  rentEmi: string
  utilitiesGroceries: string
  transportLifestyle: string
  otherExpenses: string
}

export const GOAL_KEY_LABEL: Record<GoalKey, string> = {
  wealth: 'Wealth Creation',
  retirement: 'Retirement',
  home: 'Buy a House',
  education: 'Child Education',
  marriage: 'Marriage',
}

export const GOAL_STATUS_LABEL: Record<GoalStatus, string> = {
  'on-track': 'On Track',
  'at-risk': 'At Risk',
  completed: 'Completed',
  'not-applicable': 'Not Applicable',
}

export const FLOW_LABEL: Record<FlowType, string> = {
  audit: 'Portfolio Audit',
  trades: 'Trades Audit',
  build: 'Build Portfolio',
}

/** Labels for the real `ServiceType` enum (`CaseSummary`/`CaseDetail`) — distinct from `FLOW_LABEL`. */
export const SERVICE_TYPE_LABEL: Record<ServiceType, string> = {
  portfolio_audit: 'Portfolio Audit',
  trades_audit: 'Trades Audit',
  build_portfolio: 'Build Portfolio',
}

export const LEAD_STATUS_LABEL: Record<SeriousnessStatus, string> = {
  pending: 'Pending',
  qualified: 'Qualified',
  not_needed: 'Not Needed',
  failed: 'Failed',
}

export const CALL_OUTCOME_LABEL: Record<CallOutcome, string> = {
  connected: 'Connected',
  no_answer: 'No Answer',
  follow_up_scheduled: 'Follow-up Scheduled',
  completed: 'Completed',
}

/** A real click-to-call in progress has no outcome yet — `status: 'completed'` / `outcome: null`
 * ("call happened, awaiting notes") is a real, valid, transient state, not missing data. Every
 * reader of `CallSummary.outcome` goes through this rather than indexing `CALL_OUTCOME_LABEL`
 * directly. */
export function callOutcomeLabel(outcome: CallOutcome | null): string {
  return outcome ? CALL_OUTCOME_LABEL[outcome] : 'Awaiting notes'
}

/** Query-string sentinel for filtering `GET /calls` to null-outcome ("Awaiting notes") calls —
 * matches the backend's `AWAITING_NOTES_VALUE` in `list-calls-query.dto.ts`. */
export const AWAITING_NOTES_VALUE = 'awaiting_notes'

export const CASE_STATUS_LABEL: Record<CaseStatus, string> = {
  submitted: 'Submitted',
  consultation_scheduled: 'Consultation Scheduled',
  consultation_completed: 'Consultation Completed',
  audit_ready: 'Audit Ready',
  published: 'Published',
}

export const CASE_STATUS_PIPELINE: CaseStatus[] = [
  'submitted',
  'consultation_scheduled',
  'consultation_completed',
  'audit_ready',
  'published',
]

/** Deliberately still 2-value, not `Record<FlowType, ...>` — its only real consumer,
 * `request-service-view.tsx`, never receives `flow: 'build'` (Build's intake is a multi-step
 * wizard with no single document upload, handled entirely by Phase 3's own components instead). */
export const FLOW_UPLOAD_KIND: Record<'audit' | 'trades', DocumentKind> = {
  audit: 'cas',
  trades: 'tradebook',
}

/** Inverse of `SERVICE_TYPE_TO_FLOW` (`hooks/use-my-case-detail.ts`) — for the investor
 * self-service "request an additional service" flow (`POST /cases/mine`). */
export const FLOW_TO_SERVICE_TYPE: Record<FlowType, ServiceType> = {
  audit: 'portfolio_audit',
  trades: 'trades_audit',
  build: 'build_portfolio',
}

/** The real `CaseDocumentKind` a flow's uploaded file submits as — distinct from the mock-era
 * `FLOW_UPLOAD_KIND` above. Used only by `request-service-view.tsx`'s real document upload, which
 * never receives `flow: 'build'` — see `FLOW_UPLOAD_KIND`'s comment. `audit` (a CAS statement) has
 * only ever accepted PDF; `trades` (a tradebook) accepts either a PDF or an Excel export, so its
 * kind is inferred from the file's own extension rather than fixed. */
export function inferDocKind(flow: 'audit' | 'trades', file: File): CaseDocumentKind {
  if (flow === 'audit') return 'cas_pdf'
  return file.name.toLowerCase().endsWith('.pdf') ? 'broker_pdf' : 'broker_xlsx'
}

export const DOCUMENT_KIND_LABEL: Record<DocumentKind, string> = {
  cas: 'CAS Statement',
  tradebook: 'Tradebook',
  report: 'Advisor Report',
}

export const DOCUMENT_STATUS_LABEL: Record<DocumentStatus, string> = {
  received: 'Received',
  parsed: 'Parsed',
  failed: 'Could not read',
}

/** Labels for the real `CaseDocumentKind` enum (`CaseDocumentSummary`) — distinct from `DOCUMENT_KIND_LABEL`. */
export const CASE_DOCUMENT_KIND_LABEL: Record<CaseDocumentKind, string> = {
  cas_pdf: 'CAS Statement',
  broker_xlsx: 'Tradebook',
  broker_pdf: 'Tradebook',
  advisor_report: 'Advisor Report',
}

export const CASE_HOLDING_CATEGORY_LABEL: Record<CaseHoldingCategory, string> = {
  equity: 'Equity',
  debt: 'Debt',
  hybrid: 'Hybrid',
  gold: 'Gold',
}

export const CASE_TRADE_SEGMENT_LABEL: Record<CaseTradeSegment, string> = {
  equity: 'Equity',
  fno: 'F&O',
  commodity: 'Commodity',
}

/** Mirrors `CaseDocumentDownloadDto`. `url` may be relative (local dev's storage adapter) or
 * absolute (Azure prod's signed SAS URL) — callers must normalize before using it. */
export interface CaseDocumentDownload {
  url: string
  expiresAt: string
}

/** Mirrors `CaseDocumentPasswordDto` — the CAS PDF's own document password (depository-issued,
 * not an account credential). Returned once; never cached beyond a reveal dialog's local state. */
export interface CaseDocumentPasswordResult {
  password: string
}

/** Mirrors `CaseUploadLinkSummaryDto` — metadata only, never the raw token. */
export interface CaseUploadLinkSummary {
  id: string
  createdById: string
  createdByName: string | null
  expiresAt: string
  firstUsedAt: string | null
  revokedAt: string | null
  createdAt: string
}

/** Mirrors `CaseUploadLinkCreatedDto` — the only response that ever carries the raw token. */
export interface CaseUploadLinkCreated {
  id: string
  token: string
  expiresAt: string
}

/** A live Plivo call's lifecycle — `logged` is the only value a manually-entered call ever has.
 * Every other value only ever appears on a real click-to-call row. */
export type CallLogStatus =
  | 'logged'
  | 'initiating'
  | 'ringing'
  | 'in_progress'
  | 'completed'
  | 'failed'

/** `GET /calls` row shape (`CallSummaryDto`) — no serviceType/flow field, and the recording URL is
 * never exposed (only `hasRecording`). Distinct from the mock `CallLog` above (different field
 * names throughout: `startedAt` not `calledAt`, `durationSec` not `durationMin`, etc.). `outcome`
 * is nullable — a live call has none until staff submits their notes afterward
 * (`status: 'completed'` / `outcome: null` — "call happened, awaiting notes" — is a real, valid,
 * transient state; render a fallback, never treat it as missing data). */
export interface CallSummary {
  id: string
  caseId: string
  caseReference: string
  investorName: string | null
  staffId: string
  staffName: string | null
  toNumber: string
  status: CallLogStatus
  outcome: CallOutcome | null
  startedAt: string
  durationSec: number
  notes: string | null
  hasRecording: boolean
  /** Null until Plivo's transcription callback arrives; `transcriptError` set instead means
   * transcription failed. Both null with `hasRecording: true` means "still pending." */
  transcript: string | null
  transcriptError: string | null
  createdAt: string
}

export interface CallOutcomeCount {
  /** `null` = no outcome recorded yet ("Awaiting notes") — see `callOutcomeLabel`. */
  outcome: CallOutcome | null
  count: number
}

/** `POST /cases/:caseId/calls/dial` response — always `status: 'initiating'` at this point;
 * poll `GET /calls/:callId/status` (`CallSummary`) from here. */
export interface DialCallResult {
  id: string
  status: CallLogStatus
}

/** `PATCH /calls/:callId` request body — the "wrap up this call" submission for a real Plivo
 * call. No `durationMin`: the real duration already came from the hangup webhook. */
export interface UpdateCallOutcomeInput {
  outcome: CallOutcome
  notes?: string
  followUpAt?: string
}
